import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { Booking, Child } from '../api/client'
import { fetchBookings, fetchChildren } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { EmptyStateBox } from './EmptyStateBox'

export function BookingsBox() {
  const { t } = useTranslation(['bookings', 'common'])
  const { parent } = useAuthStore()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [children, setChildren] = useState<Child[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        // Fetch both bookings and children data
        const [bookingsData, childrenData] = await Promise.all([
          fetchBookings(),
          fetchChildren()
        ]);
        
        setBookings(bookingsData || [])
        setChildren(childrenData || [])
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'bookings' }))
      } finally {
        setLoading(false)
      }
    }

    if (parent?.id) {
      fetchData()
    } else {
      setLoading(false)
    }
  }, [parent?.id, t])

  // Get child name from booking
  const getChildName = (childId: number) => {
    const child = children.find(child => child.id === childId)
    return child ? `${child.first_name} ${child.last_name}` : t('bookingsBox.unknownChild')
  }

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'finalized':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'interested':
      case 'awaiting-communication':
      case 'info-verified':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'detail_checking':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'completed':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }

  return (
    <div className="card-neobrutalism h-full flex flex-col">
      <div className="flex items-center mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
        </svg>
        <h2 className="text-2xl font-black text-neobrutalism-text">
          {t('bookingsBox.title')}
        </h2>
      </div>

      {error && (
        <div className="bg-neobrutalism-red p-4 rounded-lg border-2 border-black mb-4">
          <p className="text-neobrutalism-text font-bold">{error}</p>
        </div>
      )}

      <div className="flex-grow mb-6 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : bookings.length > 0 ? (
          <ul className="space-y-4">
            {bookings.map((booking) => (
              <li key={booking.id} className="hover:translate-y-[-2px] transition-all duration-200 border-4 border-indigo-100 p-4 shadow-sm hover:shadow-md">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center mr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                    </div>
                    <div className="w-full">
                      <p className="font-bold text-lg text-gray-800">{getChildName(booking.child)}</p>
                      
                      <div className="block sm:flex sm:items-center sm:gap-2 mt-2">
                        <p className="text-sm text-gray-600 font-medium mb-1 sm:mb-0">{t('bookingsBox.event')}:</p>
                        <p className="text-sm text-gray-600">{booking.event_name || t('bookingsBox.unknownEvent')}</p>
                      </div>
                      
                      <div className="mt-2 block sm:flex sm:items-center sm:gap-2">
                        <p className="text-sm text-gray-600 font-medium mb-1 sm:mb-0">{t('bookingsBox.status')}:</p>
                        <span className={`inline-block mt-1 sm:mt-0 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(booking.status)}`}>
                          {t(`bookingsBox.statuses.${booking.status}`)}
                        </span>
                      </div>
                      
                      <div className="mt-2 block sm:flex sm:items-center sm:gap-2">
                        <p className="text-sm text-gray-600 font-medium mb-1 sm:mb-0">{t('bookingsBox.code')}:</p>
                        <span className="text-sm font-mono text-purple-700">{booking.code}</span>
                      </div>
                      
                      {booking.in_waiting_list && (
                        <div className="mt-2 block">
                          <span className="text-sm text-red-600 font-bold flex">
                            {t('bookingsBox.waitingList')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="self-start sm:self-center mt-3 sm:mt-0 w-full sm:w-auto">
                    <Link
                      to={`/bookings/${booking.id}`}
                      className="btn-neobrutalism text-center block w-full sm:w-auto py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base bg-purple-500 hover:bg-purple-600 text-white transition-colors"
                    >
                      {t('view', { ns: 'common' })}
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyStateBox
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            }
            text={t('bookingsBox.noBookings')}
            bgColor="bg-purple-50"
            borderColor="border-purple-200"
            iconBgColor="bg-purple-100"
            iconColor="text-purple-600"
          />
        )}
      </div>

      <Link
        to="/bookings/add"
        className="btn-neobrutalism text-center bg-neobrutalism-green hover:bg-green-600 text-white transition-colors w-full text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
      >
        {t('bookingsBox.addBooking')}
      </Link>
    </div>
  )
} 